import { MAX_UPLOAD_BYTES } from '../uploadLimits';

/**
 * File Security Validator
 * Prevents stored XSS vulnerabilities and restricts uploads to documents only by:
 * - Sanitizing file names to remove malicious characters
 * - Validating MIME types using magic numbers (file signatures)
 * - Detecting suspicious file patterns
 * - Rejecting images, media, archives, and executable files
 */

/**
 * Detect double extensions with dangerous combinations
 * Prevents bypasses like malware.php.pdf or shell.exe.txt
 */
export function hasDoubleDangerousExtension(filename: string): boolean {
  const parts = filename.split('.');
  if (parts.length < 3) return false;

  const lastExt = parts[parts.length - 1].toLowerCase();
  const secondLastExt = parts[parts.length - 2].toLowerCase();

  // Dangerous combinations that could bypass filters
  const dangerousPairs = [
    ['php', 'pdf'], ['php', 'jpg'], ['php', 'png'], ['php', 'docx'], ['php', 'xlsx'],
    ['exe', 'pdf'], ['exe', 'jpg'], ['exe', 'png'], ['exe', 'txt'], ['exe', 'docx'],
    ['sh', 'pdf'], ['sh', 'jpg'], ['sh', 'png'],
    ['bat', 'pdf'], ['bat', 'txt'], ['bat', 'docx'],
    ['jsp', 'pdf'], ['jsp', 'jpg'],
    ['asp', 'pdf'], ['asp', 'jpg'],
    ['py', 'pdf'], ['py', 'jpg'],
    ['pl', 'pdf'], ['pl', 'jpg'],
    ['js', 'pdf'], ['js', 'jpg'], ['js', 'svg'],
    ['jar', 'pdf'], ['jar', 'jpg'],
    ['cgi', 'pdf'], ['cgi', 'txt']
  ];

  return dangerousPairs.some(pair =>
    (secondLastExt === pair[0] && lastExt === pair[1]) ||
    (secondLastExt === pair[1] && lastExt === pair[0])
  );
}

// Magic numbers (file signatures) for validation
const FILE_SIGNATURES: Record<string, string[]> = {
  'application/pdf': ['25504446'], // %PDF
  'image/jpeg': ['ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2', 'ffd8ffe8', 'ffd8ffdb'],
  'image/png': ['89504e47'],
  'image/gif': ['47494638'], // GIF8
  'image/webp': ['52494646', '57454250'],
  'application/zip': ['504b0304', '504b0506', '504b0708'],
  'application/x-rar-compressed': ['526172211a0700'],
  'application/gzip': ['1f8b08'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['504b0304'], // DOCX (ZIP)
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['504b0304'], // XLSX (ZIP)
  'text/plain': [], // Cannot reliably detect plain text
  'text/csv': [],
  'text/xml': [],
  'application/json': [],
  'audio/mpeg': ['fffb', 'fff3', 'fff2', 'ffa5', 'ffa4', 'fff1'],
  'audio/wav': ['52494646'], // RIFF
  'video/mp4': ['00000018', '00000020', '00000018', '6674797069736f6d'],
  'video/x-msvideo': ['52494646'], // RIFF
  'application/vnd.ms-outlook': ['d0cf11e0'], // MSG files (OLE2 format)
};

// File extensions that commonly carry executable code even if renamed
const DANGEROUS_EXTENSIONS = new Set([
  'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar',
  'msi', 'dll', 'sys', 'drv', 'app', 'bin', 'sh', 'bash', 'ps1', 'psm1',
  'php', 'php3', 'php4', 'phtml', 'pl', 'py', 'jsp', 'asp', 'htm', 'shtml', 'cgi'
]);

// SVG is treated as an archive-like format due to XML injection risks
const SVG_EXTENSIONS = new Set(['svg']);
const SVG_MIMES = new Set(['image/svg+xml']);

// Archive formats - should not be uploaded
const ARCHIVE_EXTENSIONS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz']);

// Archive MIME types
const ARCHIVE_MIMES = new Set([
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  'application/gzip',
  'application/x-tar',
  'application/x-bzip2',
  'application/x-compressed',
  'application/x-gzip'
]);

// Characters to remove from filenames (potential XSS vectors when displayed)
const FILENAME_SANITIZATION_PATTERN = /[<>"'`\\/:*?|&;$()[\]{}!~^%]/g;

export interface FileSecurity {
  isValid: boolean;
  errors: string[];
  processedFile?: File;  // File with metadata stripped (if applicable)
}


/**
 * Convert file to hex string for magic number checking
 */
async function fileToHex(file: File, bytesToRead: number = 32): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target?.result as ArrayBuffer);
      let hex = '';
      for (let i = 0; i < Math.min(bytesToRead, arr.length); i++) {
        hex += arr[i].toString(16).padStart(2, '0');
      }
      resolve(hex);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, bytesToRead));
  });
}

/**
 * Strip metadata from MP3 files using ID3 tags
 * Removes ID3 tags that could contain embedded scripts or malicious content
 */
async function stripMP3Metadata(file: File): Promise<File | null> {
  try {
    const buffer = await file.arrayBuffer();
    const bufferView = new Uint8Array(buffer);

    // Find ID3v2 tag (starts with 0x49 0x44 0x33 = "ID3")
    const isID3v2 = bufferView[0] === 0x49 && bufferView[1] === 0x44 && bufferView[2] === 0x33;

    if (!isID3v2) {
      // No ID3v2 tag found, return original file
      return file;
    }

    // Get ID3v2 tag size (syncsafe int at bytes 6-9)
    const tagSize = (
      ((bufferView[6] & 0x7f) << 21) |
      ((bufferView[7] & 0x7f) << 14) |
      ((bufferView[8] & 0x7f) << 7) |
      (bufferView[9] & 0x7f)
    ) + 10; // +10 for header

    // Skip ID3v2 tag and create new file with audio data only
    const cleanBuffer = buffer.slice(tagSize);
    const cleanFile = new File([cleanBuffer], file.name, { type: file.type });

    console.log(`[fileSecurityValidator] Stripped ID3 metadata from MP3: ${file.name}`);
    return cleanFile;
  } catch (err) {
    console.error('[fileSecurityValidator] Failed to strip MP3 metadata:', err);
    // Return original file if stripping fails
    return file;
  }
}

/**
 * Strip metadata from MP4 files
 * Note: Full MP4 metadata stripping is complex; this performs basic cleanup
 * For production, consider using FFmpeg or similar tools on the backend
 */
async function stripMP4Metadata(file: File): Promise<File | null> {
  try {
    const buffer = await file.arrayBuffer();
    const bufferView = new Uint8Array(buffer);

    // Verify MP4 signature (ftyp box)
    const hasFtyp = (
      bufferView[4] === 0x66 && bufferView[5] === 0x74 &&
      bufferView[6] === 0x79 && bufferView[7] === 0x70
    );

    if (!hasFtyp) {
      // Not a valid MP4 file, return original
      return file;
    }

    // Note: Complete MP4 metadata removal requires rewriting the entire file structure
    // This is better handled by the backend with tools like FFmpeg
    // For now, we'll log that processing occurred and return the file
    console.log(`[fileSecurityValidator] MP4 metadata stripping deferred to backend: ${file.name}`);
    return file;
  } catch (err) {
    console.error('[fileSecurityValidator] Failed to process MP4 metadata:', err);
    return file;
  }
}

/**
 * Check if SVG content contains suspicious/malicious patterns
 */
function containsSuspiciousSVGContent(svgContent: string): boolean {
  // Patterns that indicate potential XSS vectors in SVG
  const suspiciousPatterns = [
    /<script[^>]*>[\s\S]*?<\/script>/gi,
    /on\w+\s*=/gi, // Event handlers (onclick, onerror, onload, etc)
    /javascript:/gi,
    /vbscript:/gi,
  ];

  return suspiciousPatterns.some(pattern => pattern.test(svgContent));
}

/**
 * Sanitize SVG content by removing potentially malicious elements
 * Only call this if SVG files are conditionally allowed
 */
async function sanitizeSVG(file: File): Promise<File | null> {
  try {
    const svgContent = await file.text();

    // Check for suspicious patterns
    if (containsSuspiciousSVGContent(svgContent)) {
      // SVG contains suspicious content
      return null;
    }

    // If no suspicious content found, return the original file
    // In a production environment, you might want to parse and rebuild the SVG
    // to ensure only safe elements are present
    console.log(`[fileSecurityValidator] SVG file passed security checks: ${file.name}`);
    return file;
  } catch (err) {
    console.error('[fileSecurityValidator] Failed to sanitize SVG:', err);
    return null;
  }
}

/**
 * Check if file signature matches expected MIME type
 */
async function validateMimeTypeBySignature(
  file: File,
  expectedMimeType: string
): Promise<boolean> {
  // For text-based files, we cannot reliably validate by signature
  if (expectedMimeType.startsWith('text/') || 
      expectedMimeType === 'application/json' ||
      expectedMimeType === 'application/xml') {
    return true;
  }

  const signatures = FILE_SIGNATURES[expectedMimeType];
  if (!signatures || signatures.length === 0) {
    return true; // Cannot validate, allow
  }

  try {
    const hex = await fileToHex(file, 32);
    return signatures.some(sig => hex.toLowerCase().startsWith(sig.toLowerCase()));
  } catch {
    return false;
  }
}

/**
 * Sanitize filename by removing potentially dangerous characters
 */
export function sanitizeFileName(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return 'file';
  }

  // Remove path separators and null bytes
  let sanitized = filename
    .replace(/[\/\\]/g, '')
    .replace(/\0/g, '')
    .replace(/^\.+/, '') // Remove leading dots
    .trim();

  // Remove XSS-related characters
  sanitized = sanitized.replace(FILENAME_SANITIZATION_PATTERN, '_');

  // Limit length
  if (sanitized.length > 255) {
    const ext = sanitized.split('.').pop() || '';
    const name = sanitized.substring(0, 255 - ext.length - 1);
    sanitized = name + '.' + ext;
  }

  // Ensure we have at least a valid filename
  if (!sanitized || sanitized === '') {
    sanitized = 'file';
  }

  return sanitized;
}

/**
 * Get extension from filename safely
 */
function getFileExtension(filename: string): string {
  const parts = sanitizeFileName(filename).split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Comprehensive file security validation
 */
export async function validateFileSecure(
  file: File,
  allowedExtensions?: string[],
  maxSizeBytes: number = MAX_UPLOAD_BYTES
): Promise<FileSecurity> {
  const errors: string[] = [];
  let processedFile: File | undefined;

  // Basic file validation
  if (!(file instanceof File)) {
    return {
      isValid: false,
      errors: ['Invalid file object provided']
    };
  }

  if (file.size === 0) {
    errors.push('File is empty');
  }

  if (file.size > maxSizeBytes) {
    const maxMB = (maxSizeBytes / 1024 / 1024).toFixed(1);
    errors.push(`File size exceeds maximum of ${maxMB}MB`);
  }

  // Extension validation
  const extension = getFileExtension(file.name);

  if (DANGEROUS_EXTENSIONS.has(extension)) {
    errors.push(`Executable file type .${extension} is not allowed for security reasons`);
  }

  // Archive format rejection - skip if the archive type is explicitly in allowedExtensions
  if (ARCHIVE_EXTENSIONS.has(extension)) {
    const archiveExplicitlyAllowed = allowedExtensions && allowedExtensions.includes(extension);
    if (!archiveExplicitlyAllowed) {
      errors.push(`Archive formats (.${extension}) are not allowed for security reasons`);
    }
  }

  // SVG rejection (by default for security; can be conditionally allowed with sanitization)
  if (SVG_EXTENSIONS.has(extension)) {
    errors.push(`SVG files are not allowed for security reasons. Please use PNG format instead.`);
  }

  // Double extension detection
  if (hasDoubleDangerousExtension(file.name)) {
    errors.push('File has suspicious double extension pattern');
  }

  if (allowedExtensions && allowedExtensions.length > 0) {
    if (!allowedExtensions.includes(extension)) {
      errors.push(
        `File type .${extension} not allowed. Allowed types: ${allowedExtensions.join(', ')}`
      );
    }
  }

  // Archive MIME type rejection - skip if the archive type is explicitly in allowedExtensions
  if (file.type && ARCHIVE_MIMES.has(file.type)) {
    const archiveExtAllowed = allowedExtensions && allowedExtensions.some(ext =>
      ARCHIVE_EXTENSIONS.has(ext)
    );
    if (!archiveExtAllowed) {
      errors.push(`Archive file types are not allowed for security reasons`);
    }
  }

  // SVG MIME type rejection
  if (file.type && SVG_MIMES.has(file.type)) {
    errors.push(`SVG files are not allowed for security reasons. Please use PNG format instead.`);
  }

  // MIME type validation using magic numbers
  if (file.type && Object.keys(FILE_SIGNATURES).includes(file.type)) {
    const isValidMimeType = await validateMimeTypeBySignature(file, file.type);
    if (!isValidMimeType) {
      errors.push(
        `File content does not match the declared type. Expected ${file.type} but file signature suggests otherwise`
      );
    }
  }

  // Check for suspicious patterns in filename (XSS vectors)
  // Note: We check for actual attack patterns, not the bare word "script"
  // which causes false positives on legitimate words like "Subscription"
  if (file.name.includes('<') || file.name.includes('>') || 
      /(<script|javascript:|vbscript:|data:text\/html)/i.test(file.name)) {
    errors.push('Filename contains suspicious characters or patterns');
  }

  // Metadata stripping for media files (only if no validation errors so far)
  if (errors.length === 0) {
    if (extension === 'mp3' && file.type.startsWith('audio/mpeg')) {
      const cleanedFile = await stripMP3Metadata(file);
      processedFile = cleanedFile || file;
    } else if (extension === 'mp4' && file.type === 'video/mp4') {
      const cleanedFile = await stripMP4Metadata(file);
      processedFile = cleanedFile || file;
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    processedFile
  };
}

/**
 * Safe file object with sanitized metadata
 */
export interface SafeFile {
  originalFile: File;
  sanitizedName: string;
  originalName: string;
  isValid: boolean;
  validationErrors: string[];
}

/**
 * Create a safe file wrapper with sanitized metadata
 */
export async function createSafeFile(
  file: File,
  allowedExtensions?: string[],
  maxSizeBytes: number = MAX_UPLOAD_BYTES
): Promise<SafeFile> {
  const validation = await validateFileSecure(file, allowedExtensions, maxSizeBytes);
  const sanitizedName = sanitizeFileName(file.name);

  return {
    originalFile: file,
    sanitizedName,
    originalName: file.name,
    isValid: validation.isValid,
    validationErrors: validation.errors
  };
}
